import {
  CANONICAL_ORIGIN as canonicalOrigin,
  DEFAULT_OG_IMAGE as defaultOgImage,
  INDEXATION_ROUTES as indexationRoutes,
  NOINDEX_HEADER_VALUE as noindexHeaderValue,
  NOINDEX_META_VALUE as noindexMetaValue,
  SITE_NAME as siteName,
  SITEMAP_EXCLUDED_PATH_PREFIXES as sitemapExcludedPathPrefixes,
} from "@/config/seo";
import type { IndexationPolicy } from "@/interfaces/seo";

export const CANONICAL_ORIGIN = canonicalOrigin as string;
export const DEFAULT_OG_IMAGE = defaultOgImage as string;
export const SITE_NAME = siteName as string;
export const NOINDEX_META_VALUE = noindexMetaValue as string;
export const NOINDEX_HEADER_VALUE = noindexHeaderValue as string;

export const INDEXATION_ROUTE_POLICIES =
  indexationRoutes as IndexationPolicy[];
export const SITEMAP_EXCLUDED_PATH_PREFIXES = sitemapExcludedPathPrefixes as string[];

const sanitizePath = (pathname: string) => {
  const withoutQuery = pathname.split("?")[0].split("#")[0].trim();

  if (!withoutQuery) {
    return "/";
  }

  return withoutQuery.startsWith("/") ? withoutQuery : `/${withoutQuery}`;
};

export const normalizePathname = (pathname: string) => {
  const sanitized = sanitizePath(pathname);

  if (sanitized === "/") {
    return sanitized;
  }

  return sanitized.endsWith("/") ? sanitized : `${sanitized}/`;
};

const matchesPolicyPrefix = (pathname: string, prefix: string) => {
  const normalizedPath = normalizePathname(pathname);
  const normalizedPrefix = normalizePathname(prefix);

  return (
    normalizedPath === normalizedPrefix ||
    normalizedPath.startsWith(normalizedPrefix)
  );
};

export const isIndexablePath = (pathname: string) => {
  const policy = INDEXATION_ROUTE_POLICIES.find((rule) =>
    matchesPolicyPrefix(pathname, rule.prefix)
  );

  return policy ? policy.indexable : true;
};

export const getRobotsValueForPath = (pathname: string) => {
  const policy = INDEXATION_ROUTE_POLICIES.find((rule) =>
    matchesPolicyPrefix(pathname, rule.prefix)
  );

  if (policy && !policy.indexable) {
    return policy.robots || NOINDEX_META_VALUE;
  }

  return "index, follow";
};

export const shouldExcludeFromSitemap = (pathname: string) => {
  const policy = INDEXATION_ROUTE_POLICIES.find((rule) =>
    matchesPolicyPrefix(pathname, rule.prefix)
  );

  if (policy?.includeInSitemap) {
    return false;
  }

  return !isIndexablePath(pathname);
};

export const buildCanonicalUrl = (pathname: string) => {
  const normalized = normalizePathname(pathname);
  return normalized === "/"
    ? `${CANONICAL_ORIGIN}/`
    : `${CANONICAL_ORIGIN}${normalized}`;
};
